import Tooltip from '@mui/material/Tooltip';
import { ms } from 'ms';
import { type ReactNode, useState } from 'react';

import type { UUID } from '~/types/common';

import { useGetUserUtils, useQueryUser } from '~/data/user';

import { getUserDisplayName } from '@vestigas/utils';

import { UserDetails } from '@vestigas/ui';
import { UserAvatar } from '~/ui/atoms/UserAvatar';

const OPEN_DELAY_MS = 1000;
const CLOSE_DELAY_MS = 100;

type UserHoverCardProps = {
  readonly children: ReactNode;
  readonly userId: UUID;
};

function UserHoverCardQuery({ children, userId }: UserHoverCardProps) {
  const [hasOpened, setHasOpened] = useState(false);
  const { data: user } = useQueryUser(userId, {
    enabled: hasOpened,
    meta: { suppressError: true },
    staleTime: ms('10m'),
    throwOnError: false,
  });

  return (
    <Tooltip
      describeChild
      enterDelay={OPEN_DELAY_MS}
      enterNextDelay={OPEN_DELAY_MS}
      leaveDelay={CLOSE_DELAY_MS}
      placement="top-start"
      slotProps={{
        tooltip: {
          className:
            'w-72 overflow-hidden rounded-xl border border-tiga-border-lighter bg-white p-0 text-sm text-tiga-text-default shadow-lg',
          // React bubbles portal events to React ancestors: without this, a
          // click on the card reaches the control the trigger sits in. The
          // default is kept, so the e-mail link still opens the mail client.
          onClick: (event) => event.stopPropagation(),
        },
      }}
      // An empty title keeps the tooltip closed, so a user still loading or
      // not readable shows no empty card.
      title={
        user ? (
          <UserDetails
            avatar={<UserAvatar className="size-12 text-base" user={user} />}
            displayName={getUserDisplayName(user)}
            email={user.email}
            position={user.position}
          />
        ) : (
          ''
        )
      }
      onOpen={() => setHasOpened(true)}
    >
      <span>{children}</span>
    </Tooltip>
  );
}

/**
 * Shows the user's name, position and e-mail address in a hover card around
 * the trigger, fetched on the card's first open. No card shows when the viewer
 * lacks the user-read permission or the user cannot be loaded.
 */
export function UserHoverCard({ children, userId }: UserHoverCardProps) {
  const { UserUtils } = useGetUserUtils();

  return UserUtils.isUserReadAllowedUser() ? (
    <UserHoverCardQuery userId={userId}>{children}</UserHoverCardQuery>
  ) : (
    children
  );
}
